import React, { useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Printer, Tag, CheckCircle2, ArrowRight } from 'lucide-react';
import { useReactToPrint } from 'react-to-print';
import { QRCodeSVG } from 'qrcode.react';

export interface WipPalletTagData {
  transferId: string;
  palletCode: string;
  poCode: string;
  customerName: string;
  productName: string;
  fromStepName: string;
  toStepName: string;
  quantity: number;
  sheetSize?: string;
  unit?: string;
  senderName?: string;
  createdAt?: string | Date;
}

interface WipPalletTagPrintModalProps {
  isOpen: boolean;
  onClose: () => void;
  palletData: WipPalletTagData | null;
}

export function WipPalletTagPrintModal({
  isOpen,
  onClose,
  palletData,
}: WipPalletTagPrintModalProps) {
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Tem-Pallet-${palletData?.palletCode || 'WIP'}`,
  });

  if (!palletData) return null;

  const qrPayload = JSON.stringify({
    wip: palletData.transferId,
    plt: palletData.palletCode,
    po: palletData.poCode,
    qty: palletData.quantity,
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-card border-border shadow-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="w-5 h-5 text-primary" />
            Tem Pallet Bán Thành Phẩm (WIP Transfer Tag)
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            In phiếu dán ngoài cây hàng / pallet chuyển tiếp sang công đoạn kế tiếp
          </DialogDescription>
        </DialogHeader>

        {/* Khung Xem Trước Bản In Chuẩn ISO A5 Ngang */}
        <div className="overflow-auto max-h-[65vh] p-2 bg-slate-100 rounded-lg">
          <div
            ref={printRef}
            className="w-full bg-white text-slate-900 p-6 rounded-md shadow-sm border border-slate-300 font-sans print:shadow-none print:border-none print:m-0 print:p-4"
            style={{ minHeight: '140mm' }}
          >
            {/* Header Thẻ Pallet */}
            <div className="flex items-center justify-between border-b-2 border-slate-900 pb-3">
              <div className="space-y-0.5">
                <div className="text-[11px] font-black uppercase tracking-wider text-slate-700">
                  CÔNG TY TNHH ART PRESTO VIỆT NAM
                </div>
                <h1 className="text-xl font-black uppercase text-slate-900 tracking-tight">
                  THẺ BÁN THÀNH PHẨM SÀN MÁY
                </h1>
                <div className="text-[10px] text-slate-500 italic">
                  Biểu mẫu kiểm soát luân chuyển WIP nội bộ nhà xưởng
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="border border-slate-300 p-1 rounded bg-white">
                  <QRCodeSVG value={qrPayload} size={76} level="M" />
                </div>
                <div className="text-right">
                  <div className="text-[10px] font-bold text-slate-500 uppercase">Mã Pallet</div>
                  <div className="font-mono text-base font-black text-slate-900">{palletData.palletCode}</div>
                  <div className="font-mono text-xs text-slate-600">Số: {palletData.transferId}</div>
                </div>
              </div>
            </div>

            {/* Thông Tin Chi Tiết Lệnh Sản Xuất */}
            <div className="grid grid-cols-2 gap-3 py-3 border-b border-slate-300 text-xs">
              <div>
                <span className="text-[10px] font-bold text-slate-500 uppercase block">Lệnh Sản Xuất (PO):</span>
                <span className="font-mono font-black text-base text-blue-900">{palletData.poCode}</span>
              </div>
              <div>
                <span className="text-[10px] font-bold text-slate-500 uppercase block">Khách Hàng:</span>
                <span className="font-bold text-slate-900 text-sm">{palletData.customerName}</span>
              </div>
              <div className="col-span-2">
                <span className="text-[10px] font-bold text-slate-500 uppercase block">Tên Sản Phẩm / Bài In:</span>
                <span className="font-extrabold text-slate-900 text-sm">{palletData.productName}</span>
              </div>
            </div>

            {/* Luồng Chuyển Giao Công Đoạn */}
            <div className="my-3 p-3 bg-slate-50 border border-slate-300 rounded-lg flex items-center justify-between">
              <div className="space-y-0.5">
                <span className="text-[10px] font-bold text-slate-500 uppercase block">Công Đoạn Giao:</span>
                <span className="font-black text-sm text-slate-900">{palletData.fromStepName}</span>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400 mx-2" />
              <div className="space-y-0.5 text-right">
                <span className="text-[10px] font-bold text-slate-500 uppercase block">Công Đoạn Tiếp Nhận:</span>
                <span className="font-black text-sm text-emerald-800">{palletData.toStepName}</span>
              </div>
            </div>

            {/* Số Lượng & Khổ Phôi */}
            <div className="grid grid-cols-3 gap-3 p-3 bg-amber-50 border-2 border-amber-300 rounded-lg text-center my-3">
              <div>
                <div className="text-[10px] font-bold text-amber-800 uppercase">Khổ Phôi / Tờ</div>
                <div className="font-mono font-black text-sm text-slate-900 mt-0.5">
                  {palletData.sheetSize || '—'}
                </div>
              </div>
              <div className="border-x border-amber-300">
                <div className="text-[10px] font-bold text-amber-800 uppercase">Số Lượng Pallet</div>
                <div className="font-mono font-black text-2xl text-emerald-700 mt-0.5">
                  {palletData.quantity.toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-bold text-amber-800 uppercase">Đơn Vị Tính</div>
                <div className="font-bold text-sm text-slate-900 mt-0.5">
                  {palletData.unit || 'Tờ in'}
                </div>
              </div>
            </div>

            {/* Chữ Ký Xác Nhận */}
            <div className="grid grid-cols-3 gap-4 pt-4 mt-2 text-center text-xs border-t border-slate-300">
              <div className="space-y-12">
                <div className="font-bold uppercase text-[11px] text-slate-700">Người Bàn Giao</div>
                <div className="font-semibold text-slate-800">{palletData.senderName || 'Thợ máy giao'}</div>
              </div>
              <div className="space-y-12">
                <div className="font-bold uppercase text-[11px] text-slate-700">Người Tiếp Nhận</div>
                <div className="text-slate-400 italic">(Ký & ghi rõ họ tên)</div>
              </div>
              <div className="space-y-12">
                <div className="font-bold uppercase text-[11px] text-slate-700">KCS / Giám Sát</div>
                <div className="text-slate-400 italic">(Xác nhận chất lượng)</div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-4 pt-2 border-t border-dashed border-slate-300 flex items-center justify-between text-[9px] text-slate-500">
              <span>Ngày giờ tạo: {new Date(palletData.createdAt || Date.now()).toLocaleString('vi-VN')}</span>
              <span>Hệ thống APV MES &bull; In nhãn dán Pallet</span>
            </div>
          </div>
        </div>

        <DialogFooter className="flex items-center justify-between sm:justify-between border-t border-border pt-3">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Đóng
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => handlePrint()}
            className="bg-primary text-primary-foreground font-bold gap-1.5 shadow-sm"
          >
            <Printer className="w-4 h-4" />
            In Tem Pallet (A5/Decal)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
